import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '../utils/api';
import { Card, PageHeader, Btn, Badge, Modal, Input, Select } from '../components/Layout';

const VISA_OPTS  = ['H1B', 'EAD', 'GC', 'USC', 'TN', 'OPT'];
const STATUS_OPTS = ['Available', 'Submitted', 'Interviewing', 'Placed', 'On Project'];

const statusColor = s => ({ Available:'var(--green)', Submitted:'var(--accent)', Interviewing:'var(--amber)', Placed:'var(--purple)', 'On Project':'var(--muted)' }[s] || 'var(--muted)');

export default function Bench() {
  const { company, mode } = useOutletContext();
  const [consultants, setConsultants] = useState([]);
  const [filtered,    setFiltered]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [showAdd,     setShowAdd]     = useState(false);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [requirement, setRequirement] = useState('');
  const [matches,     setMatches]     = useState([]);
  const [searchSkill, setSearchSkill] = useState('');
  const [filterVisa,  setFilterVisa]  = useState('');
  const [filterStatus,setFilterStatus]= useState('Available');
  const [newC, setNewC] = useState({ name:'', email:'', phone:'', skill:'', visa:'H1B', rateC2C:'', rateW2:'', location:'', availableFrom:'', notes:'' });

  useEffect(() => { loadConsultants(); }, [company]);

  useEffect(() => {
    let f = [...consultants];
    if (searchSkill) f = f.filter(c => c.skill?.toLowerCase().includes(searchSkill.toLowerCase()) || c.title?.toLowerCase().includes(searchSkill.toLowerCase()));
    if (filterVisa)  f = f.filter(c => (c.visa || c.tags?.includes(filterVisa)) === filterVisa || c.visa === filterVisa);
    if (filterStatus) f = f.filter(c => (c.status || 'new').toLowerCase() === filterStatus.toLowerCase() || c.status === filterStatus);
    setFiltered(f);
  }, [consultants, searchSkill, filterVisa, filterStatus]);

  const loadConsultants = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/leads?type=recruiting&company=${encodeURIComponent(company)}&limit=200`);
      setConsultants(r.data.leads || []);
    } catch {}
    setLoading(false);
  };

  const addConsultant = async () => {
    if (!newC.name || !newC.email) return alert('Name and email are required');
    try {
      await api.post('/leads', { ...newC, company, type: 'recruiting', source: 'manual' });
      setShowAdd(false);
      setNewC({ name:'', email:'', phone:'', skill:'', visa:'H1B', rateC2C:'', rateW2:'', location:'', availableFrom:'', notes:'' });
      loadConsultants();
    } catch (err) { alert(err.response?.data?.error || 'Failed to add consultant'); }
  };

  const findMatches = () => {
    if (!requirement.trim()) return;
    const keywords = requirement.toLowerCase().split(/\s+/).filter(w => w.length > 3);
    const scored = consultants.map(c => {
      const text = `${c.skill} ${c.title} ${c.notes}`.toLowerCase();
      const score = keywords.filter(k => text.includes(k)).length;
      return { ...c, matchScore: score };
    }).filter(c => c.matchScore > 0).sort((a, b) => b.matchScore - a.matchScore);
    setMatches(scored);
  };

  const stats = {
    total:       consultants.length,
    available:   consultants.filter(c => (c.status || 'new') === 'Available' || c.status === 'new').length,
    submitted:   consultants.filter(c => c.status === 'Submitted').length,
    placed:      consultants.filter(c => c.status === 'Placed').length,
  };

  const isMavenOnly = company === 'Maven Group Global';

  if (isMavenOnly) return (
    <div style={{ padding: 28 }}>
      <PageHeader title="🏪 Bench Board" sub="Consultant inventory" />
      <Card style={{ textAlign: 'center', padding: 60 }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>👔</div>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Bench Board is for Recruiting Companies</div>
        <div style={{ color: 'var(--muted)', fontSize: 14 }}>
          Switch to <strong>B2techsoftLLC</strong> or <strong>Xolosoft LLC</strong> to manage your consultant bench.
        </div>
      </Card>
    </div>
  );

  return (
    <div style={{ padding: 28 }}>
      <PageHeader title="🏪 Bench Board" sub={`${company} — Available Consultants`}>
        <Btn small onClick={() => setShowBroadcast(true)}>📣 Broadcast Requirement</Btn>
        <Btn primary small onClick={() => setShowAdd(true)}>+ Add Consultant</Btn>
      </PageHeader>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Total on Bench', value: stats.total,     color: 'var(--accent)'  },
          { label: 'Available Now',  value: stats.available,  color: 'var(--green)'   },
          { label: 'Submitted',      value: stats.submitted,  color: 'var(--amber)'   },
          { label: 'Placed',         value: stats.placed,     color: 'var(--purple)'  },
        ].map(s => (
          <Card key={s.label} style={{ borderTop: `3px solid ${s.color}`, textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: 32, fontWeight: 800, color: s.color, fontFamily: "'Space Mono', monospace" }}>{s.value}</div>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card style={{ marginBottom: 20, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: 1, minWidth: 180 }}>
          <label style={{ display: 'block', fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 5 }}>Search Skill</label>
          <input value={searchSkill} onChange={e => setSearchSkill(e.target.value)} placeholder="Java, SAP, Python..."
            style={{ width: '100%', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 7, padding: '9px 12px', color: 'var(--text)', fontSize: 13, outline: 'none' }} />
        </div>
        <div style={{ minWidth: 140 }}>
          <label style={{ display: 'block', fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 5 }}>Visa</label>
          <select value={filterVisa} onChange={e => setFilterVisa(e.target.value)}
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 7, padding: '9px 12px', color: 'var(--text)', fontSize: 13, outline: 'none' }}>
            <option value="">All Visas</option>
            {VISA_OPTS.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div style={{ minWidth: 160 }}>
          <label style={{ display: 'block', fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 5 }}>Status</label>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 7, padding: '9px 12px', color: 'var(--text)', fontSize: 13, outline: 'none' }}>
            <option value="">All Statuses</option>
            {STATUS_OPTS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <Btn small onClick={() => { setSearchSkill(''); setFilterVisa(''); setFilterStatus(''); }}>Clear</Btn>
      </Card>

      {/* Table */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontWeight: 700 }}>Consultants ({filtered.length})</div>
        </div>
        {loading ? (
          <div style={{ color: 'var(--muted)', textAlign: 'center', padding: 40 }}>Loading bench...</div>
        ) : filtered.length === 0 ? (
          <div style={{ color: 'var(--muted)', textAlign: 'center', padding: 40 }}>
            No consultants match your filters. <Btn small onClick={() => setShowAdd(true)}>Add First Consultant</Btn>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {['Name', 'Skill', 'Visa', 'C2C Rate', 'W2 Rate', 'Location', 'Available', 'Status', 'Actions'].map(h => (
                    <th key={h} style={{ color: 'var(--muted)', fontWeight: 600, padding: '8px 12px', textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: .5, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((c, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)22' }}>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ fontWeight: 600 }}>{c.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>{c.email}</div>
                    </td>
                    <td style={{ padding: '10px 12px', color: 'var(--accent)', fontWeight: 600 }}>{c.skill || c.title || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>{c.visa ? <Badge label={c.visa} color="var(--purple)" /> : '—'}</td>
                    <td style={{ padding: '10px 12px', color: 'var(--green)' }}>{c.rateC2C ? `$${c.rateC2C}/hr` : '—'}</td>
                    <td style={{ padding: '10px 12px', color: 'var(--amber)' }}>{c.rateW2 ? `$${c.rateW2}/hr` : '—'}</td>
                    <td style={{ padding: '10px 12px', color: 'var(--mid)' }}>{c.location || '—'}</td>
                    <td style={{ padding: '10px 12px', color: 'var(--muted)' }}>
                      {c.availableFrom ? new Date(c.availableFrom).toLocaleDateString() : 'Immediate'}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <Badge label={c.status || 'New'} color={statusColor(c.status)} />
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Btn small onClick={() => window.open(`mailto:${c.email}`)}>✉️</Btn>
                        {c.phone && <Btn small onClick={() => window.open(`tel:${c.phone}`)}>📞</Btn>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Add Consultant Modal */}
      {showAdd && (
        <Modal title="➕ Add Consultant" onClose={() => setShowAdd(false)} width={600}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Input label="Full Name *" value={newC.name} onChange={e => setNewC(n => ({ ...n, name: e.target.value }))} placeholder="Raj Patel" />
            <Input label="Email *" value={newC.email} onChange={e => setNewC(n => ({ ...n, email: e.target.value }))} placeholder="raj@email.com" />
            <Input label="Phone" value={newC.phone} onChange={e => setNewC(n => ({ ...n, phone: e.target.value }))} placeholder="+1-214-555-0000" />
            <Input label="Primary Skill" value={newC.skill} onChange={e => setNewC(n => ({ ...n, skill: e.target.value }))} placeholder="Java/Spring, SAP FICO..." />
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 5 }}>Visa Status</label>
              <select value={newC.visa} onChange={e => setNewC(n => ({ ...n, visa: e.target.value }))}
                style={{ width: '100%', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 7, padding: '10px 12px', color: 'var(--text)', fontSize: 13, outline: 'none' }}>
                {VISA_OPTS.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <Input label="Location" value={newC.location} onChange={e => setNewC(n => ({ ...n, location: e.target.value }))} placeholder="Dallas TX / Remote" />
            <Input label="C2C Rate ($/hr)" type="number" value={newC.rateC2C} onChange={e => setNewC(n => ({ ...n, rateC2C: e.target.value }))} placeholder="75" />
            <Input label="W2 Rate ($/hr)" type="number" value={newC.rateW2} onChange={e => setNewC(n => ({ ...n, rateW2: e.target.value }))} placeholder="60" />
            <Input label="Available From" type="date" value={newC.availableFrom} onChange={e => setNewC(n => ({ ...n, availableFrom: e.target.value }))} />
          </div>
          <div style={{ marginTop: 4 }}>
            <Input label="Notes" value={newC.notes} onChange={e => setNewC(n => ({ ...n, notes: e.target.value }))} placeholder="Additional details, past projects, preferences..." />
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <Btn small onClick={() => setShowAdd(false)}>Cancel</Btn>
            <Btn primary small onClick={addConsultant}>Add to Bench</Btn>
          </div>
        </Modal>
      )}

      {/* Broadcast Requirement Modal */}
      {showBroadcast && (
        <Modal title="📣 Broadcast Requirement" onClose={() => { setShowBroadcast(false); setMatches([]); setRequirement(''); }} width={680}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 5 }}>Paste Job Requirement</label>
            <textarea value={requirement} onChange={e => setRequirement(e.target.value)} rows={6}
              style={{ width: '100%', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 7, padding: '10px 12px', color: 'var(--text)', fontSize: 13, outline: 'none', resize: 'vertical', fontFamily: 'inherit' }}
              placeholder="Paste the full job requirement here. Example: Looking for a Java/Spring developer with 8+ years experience, H1B or GC, remote, rate up to $80/hr C2C, start ASAP..." />
          </div>
          <Btn small onClick={findMatches}>🔍 Find Matches on Bench</Btn>

          {matches.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <div style={{ fontWeight: 700, marginBottom: 12, color: 'var(--green)' }}>✅ {matches.length} consultants matched</div>
              {matches.map((m, i) => (
                <div key={i} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{m.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>{m.skill} · {m.visa} · {m.location || 'Remote'}</div>
                    <div style={{ fontSize: 12, color: 'var(--green)' }}>
                      {m.rateC2C ? `C2C: $${m.rateC2C}/hr` : ''}{m.rateC2C && m.rateW2 ? ' · ' : ''}{m.rateW2 ? `W2: $${m.rateW2}/hr` : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <Badge label={`${m.matchScore} match${m.matchScore !== 1 ? 'es' : ''}`} color="var(--accent)" />
                    <Btn small onClick={() => window.open(`mailto:${m.email}?subject=Requirement Match — ${company}&body=${encodeURIComponent(requirement)}`)}>✉️ Email</Btn>
                  </div>
                </div>
              ))}
              <div style={{ marginTop: 12 }}>
                <Btn primary small onClick={() => {
                  const emails = matches.map(m => m.email).join(',');
                  window.open(`mailto:${emails}?subject=New Requirement — ${company}&body=${encodeURIComponent(requirement)}`);
                }}>✉️ Email All {matches.length} Matches</Btn>
              </div>
            </div>
          )}
          {requirement && matches.length === 0 && (
            <div style={{ marginTop: 16, color: 'var(--muted)', fontSize: 13 }}>
              No matches found on current bench. Try different keywords or add more consultants.
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
